import constants from '@Common/Core/Constant';

import { dimensionFilterValue, filterValue } from '@Common/Entities/FilterEntities';
import { compareEnumValues, MarkerImportance } from '@Common/Enums';

import * as commonUtils from '@Common/Helpers/CommonUtils';
import timeHelper from '@Common/Helpers/TimeHelper';
import { BaseDrillDown, ComponentData, DateTimeInterval, DimensionFilterValues, MarkerDocument, MetricSeries, SeriesFilter } from '@InstanceFactory';
import urlHelper from '@Common/Helpers/UrlHelper';

export class ProcessMarketData {
    public static processResults(
        componentData: ComponentData,
        seriesFilters: SeriesFilter[],
        drillDownIds: string[],
        componentFilters: DimensionFilterValues[],
        timeInterval: DateTimeInterval,
        seriesList: MetricSeries[],
        drillDowns: BaseDrillDown[],
        isGrid: boolean
    ): void {
        try {
            const drillDownIdSet = new Set(drillDownIds);
            const currentDrillDowns = _(drillDowns)
                .filter(drillDown => drillDownIdSet.has(drillDown.id))
                .value();
            const currentMarkersDrillDowns = [];
            const currentDrill = _(drillDowns).find(
                drillDown =>
                    (drillDownIds.length === 1 && drillDown.id === drillDownIds[0]) ||
                    (drillDownIds.length > 1 && drillDown.chartDimensionId === constants.dimension2 && drillDownIdSet.has(drillDown.id))
            );
            if (currentDrill != null) {
                currentMarkersDrillDowns.push(currentDrill);
            }

            if (componentFilters == null) {
                componentFilters = [];
            }
            const markerSettings = altosoft.model.insightMeta.markerSettings;
            const markers = _(altosoft.model.insightMeta.markers)
                .filter(
                    marker =>
                        compareEnumValues(marker.importance, markerSettings.importance, MarkerImportance) !== -1 &&
                        markerSettings.markersType.hasFlag(marker.type)
                )
                .value();

            _(componentData.dataRows).forEach(row => {
                for (let seriesFilterIndex = 0; seriesFilterIndex < seriesFilters.length; seriesFilterIndex++) {
                    const seriesFilter = seriesFilters[seriesFilterIndex];
                    const lastLevelFilters = _(currentDrillDowns)
                        .filter(currentDrillDown => _(row.dimensions).some(dimensionCell => dimensionCell.drillId === currentDrillDown.id))
                        .map(currentDrillDown => {
                            const dimCell = _(row.dimensions).find(dimensionCell => dimensionCell.drillId === currentDrillDown.id);
                            return dimensionFilterValue(currentDrillDown.id, [filterValue(dimCell.key, (dimCell as any).name)]); // name ??
                        })
                        .value();

                    if (seriesFilter.dimensionValues != null) {
                        lastLevelFilters.addRange(seriesFilter.dimensionValues);
                    }

                    const filters = componentFilters.slice();
                    filters.addRange(lastLevelFilters);

                    const hasTimeDrillDown = _(filters).some(filter => timeHelper.isTimeDrillDown(filter.id));

                    _(markers)
                        .filter(marker => {
                            if (
                                !marker.forAllMetrics &&
                                !_(seriesList)
                                    .filter(series => series.metric != null)
                                    .some(series => series.seriesId === seriesFilter.seriesId && series.metric.id === marker.metric.id)
                            ) {
                                return false;
                            }
                            const dimensionValues = marker.dimensionFilter.dimensionValues;
                            const commonFilterPairs = dimensionValues
                                .map(filterValue => {
                                    const componentFilter = _(filters).find(item => item.id === filterValue.id);
                                    return { markerFilter: filterValue, componentFilter: componentFilter };
                                })
                                .filter(commonFilterPair => commonFilterPair.componentFilter != null && commonFilterPair.markerFilter != null);

                            const hasCommonFilterValues =
                                commonFilterPairs.length > 0 &&
                                !_(commonFilterPairs).some(
                                    commonFilterPair =>
                                        !_.some(commonFilterPair.markerFilter.values, value1 =>
                                            _.some(
                                                commonFilterPair.componentFilter.values,
                                                value2 => value2 != null && value1 != null && value1.id === value2.id
                                            )
                                        )
                                );

                            const interval = row.data[seriesFilterIndex].interval;
                            return (
                                ((dimensionValues.length === 0 && (hasTimeDrillDown || filters.length === 0)) || hasCommonFilterValues) &&
                                (!markerSettings.useTopLevelMarkers || commonFilterPairs.length === dimensionValues.length) &&
                                marker.markerTime >= interval.from &&
                                marker.markerTime < interval.to
                            );
                        })
                        .forEach(marker => {
                            if (marker.forAllMetrics && isGrid && row.dimensions != null && row.dimensions.length > 0) {
                                ProcessMarketData.addMarkerToData(marker, row.dimensions[0] as any);
                            } else {
                                ProcessMarketData.addMarkerToData(marker, row.data[seriesFilterIndex] as any);
                            }
                        });
                }
            });
        } catch (e) {
            commonUtils.log('Could not process markers');
        }
    }

    private static addMarkerToData(marker: MarkerDocument, cell: { markers: MarkerDocument[]; markerIcon: string }): void {
        if (cell.markers == null) {
            cell.markers = [];
        }
        const existMarker = _.find(cell.markers, { id: marker.id });
        if (existMarker) {
            return;
        }
        cell.markers.push(marker);
        cell.markerIcon =
            cell.markers.length === 1
                ? ProcessMarketData.getMarkerIconUrl(cell.markers[0].type)
                : cell.markers.length > 1
                ? ProcessMarketData.getMarkerIconUrl('markers')
                : undefined;
    }

    public static getMarkerIconUrl(markerIconName: string | number): string {
        return urlHelper.getImageUrl(`MarkerIcons/${markerIconName}.png`);
    }
}
